import { JSX } from "react";
import { appContext } from "../../../../AppContext";
import { IbssComponent } from "../../../../Components/Core/BaseComponent/IbssComponent";
import {
    Button,
    ButtonBar,
    Card,
    Dialog,
    DialogContent,
    DialogFooter,
    GridContainer,
    GridItem,
    RightButtons,
    Select,
    TextField,
} from "@iconics/components";
import { IGraphNode } from "./GraphView";
import { LanguageValuesHelper } from "@iconics/localisation";
import {
    IRelationshipData,
    IRelationshipData as Relationship,
} from "./DataModels";
import { TypeHelper, Validator } from "@iconics/core";

class AddRelationshipDialog extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private languageValuesHelper: LanguageValuesHelper;
    private validator = new Validator(appContext().core.validationOptions);

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(
            appContext().localisation,
        );
        this.state = {
            relationshipId: "",
            relationshipError: "",
        };
    }

    public componentDidMount(): void
    {
        this.validator
            .addInput(
                "relationshipId",
                () => this.state.relationshipId,
                (i) => this.setState({ relationshipError: i }),
            )
            .isRequired();
    }

    public componentDidUpdate(prevProps: Readonly<IProps>): void
    {
        if (!prevProps.open && this.props.open)
        {
            this.handleOpen();
        }
    }

    private handleOpen(): void
    {
        if (!this.props.source || !this.props.target)
        {
            this.setState({
                relationshipId: "",
            });
        }
else
{
            this.setState({
                relationshipId: "",
            });
        }
    }

    private handleCancel(): void
    {
        this.props.onClose();
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
            return;
        await this.loading(async () =>
        {
            if (this.props.source && this.props.target && this.state.relationshipId)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.relationships.post(
                    {
                        nodeId: this.props.source.id,
                        tenancy: appContext().tenantId,
                        body: {
                            SecondaryNodeid: this.props.target.id,
                            RelationshipId: this.state.relationshipId,
                        },
                    },
                );
                this.props.onSave(this.state.relationshipId);
            }
        });
    }

    public render(): JSX.Element
    {
        const getRelationshipName = (i: Relationship) =>
        {
            const displayName = this.languageValuesHelper.fromString(i.DisplayName);
            const name = this.languageValuesHelper.getValue(displayName) || i.Name;
            return `${name} (${(TypeHelper.toBoolean(i.IsEnabled) ? this.labels.Platform_Enabled : this.labels.Platform_Disabled)})`;
        };

        const getNodeName = (i: IGraphNode | undefined) =>
        {
            if (!i)
                return '';
            return this.languageValuesHelper.getValue(i.displayName) || i.name;
        };

        return (
            <Dialog isOpen={this.props.open} onOpenChange={() => this.handleCancel()}>
                <DialogContent>
                    <Card
                        title={this.labels.Platform_Graph_AddRelationshipTitle}
                        subTitle={this.labels.Platform_Graph_AddRelationshipSubTitle}
                        details={this.labels.Platform_Graph_AddRelationshipDetails}
                    >
                        <GridContainer>
                            <GridItem expanded={6}>
                                <TextField
                                    label={this.labels.Relationship_Source}
                                    placeholder={this.labels.Relationship_Source}
                                    value={getNodeName(this.props.source)}
                                    disabled
                                />
                            </GridItem>
                            <GridItem expanded={6}>
                                <TextField
                                    label={this.labels.Relationship_Destination}
                                    placeholder={this.labels.Relationship_Destination}
                                    value={getNodeName(this.props.target)}
                                    disabled
                                />
                            </GridItem>
                            <GridItem expanded={6}>
                                <Select
                                    label={this.labels.Relationship_Name}
                                    placeholder={this.labels.Relationship_Name}
                                    items={this.props.relationships.map((i) => ({
                                        value: i.Id,
                                        label: getRelationshipName(i),
                                    }))}
                                    value={
                                        this.state.relationshipId ? [this.state.relationshipId] : []
                                    }
                                    onSelect={(e) =>
                                        this.setState({ relationshipId: e.value }, () =>
                                            this.validator.validate("relationshipId"),
                                        )
                                    }
                                    invalid={!!this.state.relationshipError}
                                    invalidMessage={this.state.relationshipError}
                                    required
                                />
                            </GridItem>
                        </GridContainer>
                    </Card>
                </DialogContent>
                <DialogFooter>
                    <ButtonBar>
                        <RightButtons>
                            <Button styleType="outlined" onClick={() => this.handleCancel()}>
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType="filled"
                                disabled={!this.validator.isValid}
                                onClick={() => this.handleSave()}
                            >
                                {this.labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </DialogFooter>
            </Dialog>
        );
    }
}

export default AddRelationshipDialog;

export interface IProps
{
    open: boolean;
    onClose: () => void;
    onSave: (id: string) => void;
    source?: IGraphNode;
    target?: IGraphNode;
    relationships: IRelationshipData[];
}

export interface IState
{
    relationshipId: string;
    relationshipError: string;
}
